import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Login as EnterIcon, Visibility as ViewIcon } from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';
import { LEVEL, creditChip } from './NodeOverview';
import { useAuth } from '../contexts/AuthContext';
import { OPEN_LABEL, openLevel } from '../utils/openLevel';

/**
 * One node BELOW yours, read-only.
 *
 * The look from above: a level beneath yours, read from your own session. Nothing on it
 * is clinical. To work inside the level — its own dashboard, and for an office its
 * patients — press Open, which is a real, audited portal switch (see utils/openLevel).
 */
export default function NodeDetail() {
  const { scope, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entering, setEntering] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await hierarchyApi.getNode(scope, id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open that');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [scope, id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return null;

  const { node, ancestors = [], children = [], people = {}, totals = {} } = data;
  const level = LEVEL[node.scope] || LEVEL.clinic;
  const chip = creditChip(node);
  const workers = [...(people.providers || []), ...(people.staff || [])];

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/')} sx={{ mb: 1 }}>
        Back to overview
      </Button>

      {!!ancestors.length && (
        <Breadcrumbs sx={{ mb: 1 }}>
          {ancestors.map((a) => (
            <Typography key={a.id} variant="body2" color="text.secondary">{a.name}</Typography>
          ))}
          <Typography variant="body2" fontWeight={600}>{node.name}</Typography>
        </Breadcrumbs>
      )}

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ color: level.color, display: 'flex' }}>{level.icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={700}>{node.name}</Typography>
          <Typography variant="body2" color="text.secondary">{level.label}</Typography>
        </Box>
        <Chip label={chip.text} color={chip.tone === 'default' ? undefined : chip.tone}
          variant={chip.tone === 'default' ? 'outlined' : 'filled'} />
        {/* This page is the look from above. Opening it is the other thing: working
            inside it as its administrator, on its own dashboard. */}
        {OPEN_LABEL[node.scope] && node.is_active !== false
          && String(node.id) !== String(user?.node_id) && (
          <Button variant="contained" startIcon={<EnterIcon />} disabled={entering}
            onClick={async () => {
              setEntering(true);
              try {
                await openLevel(node);
              } catch (err) {
                toast.error(err.response?.data?.detail || `Could not open ${node.name}`);
                setEntering(false);
              }
            }}>
            {entering ? 'Opening…' : OPEN_LABEL[node.scope]}
          </Button>
        )}
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { v: totals.offices ?? 0, l: 'Offices' },
          { v: totals.providers ?? 0, l: 'Providers' },
          { v: totals.staff ?? 0, l: 'Staff' },
          { v: totals.sessions_used ?? 0, l: 'Sessions used' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.l}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h4" fontWeight={700}>{s.v}</Typography>
              <Typography variant="caption" color="text.secondary">{s.l}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {node.scope !== 'clinic' && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={700}>What is inside</Typography>
          </CardContent>
          <Divider />
          {!children.length ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Nothing yet.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {children.map((c) => {
                    const lv = LEVEL[c.scope] || LEVEL.clinic;
                    const cc = creditChip(c);
                    return (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ width: 40, color: lv.color }}>{lv.icon}</TableCell>
                        <TableCell>
                          <Link component="button" underline="hover"
                            onClick={() => navigate(`/node/${c.scope}/${c.id}`)}>
                            <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={lv.label}
                            sx={{ height: 20, fontSize: 11, borderColor: lv.color, color: lv.color }} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={cc.text}
                            color={cc.tone === 'default' ? undefined : cc.tone}
                            variant={cc.tone === 'default' ? 'outlined' : 'filled'}
                            sx={{ height: 20, fontSize: 11 }} />
                        </TableCell>
                        <TableCell align="right" sx={{ width: 48 }}>
                          <ViewIcon fontSize="small" color="action"
                            onClick={() => navigate(`/node/${c.scope}/${c.id}`)}
                            sx={{ cursor: 'pointer' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Who works here</Typography>
          <Typography variant="body2" color="text.secondary">
            Staffing only. Patient records belong to the office and are not shown here.
          </Typography>
        </CardContent>
        <Divider />
        {!workers.length ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Nobody assigned yet.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Office</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workers.map((w) => (
                  <TableRow key={`${w.user_id}-${w.office_id}`} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{w.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{w.email}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{w.office_name}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
